<script setup lang="ts">
  import { computed, watch } from "vue";
  import { Loader2, Mic } from "lucide-vue-next";
  import { useToastsStore } from "@/stores/toastsStore";
  import useVoiceTranscription from "@/composables/useVoiceTranscription";
  import type { TranscriptParts } from "@utils/gladiaLiveSession";

  // `live` streams the words as they are spoken instead of waiting for each
  // sentence to be finalised. Opt-in: it costs a little accuracy, which only
  // makes sense where the text is being watched while dictated.
  const props = defineProps<{ disabled?: boolean; live?: boolean }>();
  const model = defineModel<string>({ required: true });
  // Lets the surrounding composer show the dictation state and the words Gladia
  // is still revising. The hypothesis never enters the field, so an unsettled
  // guess can neither be sent nor survive a cancellation unnoticed.
  const recording = defineModel<boolean>("recording", { default: false });
  const hypothesis = defineModel<string>("hypothesis", { default: "" });

  const toast = useToastsStore();
  const { isRecording, isStopping, isStarting, error, start, stop, cancel } = useVoiceTranscription({
    partialTranscripts: props.live === true,
  });

  let textBeforeRecording = "";
  let lastAppliedText: string | null = null;

  const applyConfirmed = (confirmed: string) => {
    if (!confirmed) return;
    lastAppliedText = textBeforeRecording ? `${textBeforeRecording} ${confirmed}` : confirmed;
    model.value = lastAppliedText;
  };

  const applyTranscript = ({ confirmed, hypothesis: open }: TranscriptParts) => {
    hypothesis.value = open;
    applyConfirmed(confirmed);
  };

  // Stays true while the final transcript is still on its way, so the words on
  // screen do not vanish between the click and the last utterance.
  const isDictating = computed(() => isRecording.value || isStopping.value);

  const toggleRecording = async () => {
    if (props.disabled || isStopping.value || isStarting.value) return;

    if (isRecording.value) {
      // Gladia may never send its final transcript — a timeout resolves stop()
      // with the partials instead — so the returned text is the last word on
      // what was said, not the callback.
      applyConfirmed(await stop());
      return;
    }

    textBeforeRecording = model.value.trim();
    await start(applyTranscript);
  };

  // Nothing spoken should vanish when the dictation ends, whichever path ended
  // it: taking the field over, sending, or a Gladia timeout that resolves stop()
  // without ever sending a final transcript.
  const commitHypothesis = () => {
    if (!hypothesis.value) return;

    lastAppliedText = [model.value, hypothesis.value].filter(Boolean).join(" ");
    hypothesis.value = "";
    model.value = lastAppliedText;
  };

  const cancelDictation = () => {
    if (!isRecording.value) return;

    cancel();
    // Lowered here rather than left to the watcher below: the composer swaps its
    // preview back for the textarea on this flag and focuses it on the next tick.
    recording.value = false;
    commitHypothesis();
  };

  defineExpose({ cancelDictation });

  const title = computed(() => (isRecording.value ? "Stop dictation" : "Dictate"));

  watch(
    isDictating,
    (value) => {
      recording.value = value;
      if (!value) commitHypothesis();
    },
    { immediate: true },
  );

  // The field was edited or cleared outside of dictation (typing, message sent,
  // conversation switched): stop feeding it stale transcript text.
  watch(model, (value) => {
    if (!isRecording.value) return;
    if (value === lastAppliedText) return;
    cancel();
  });

  watch(error, (value) => {
    if (value === "microphone-denied") toast.error("Microphone access denied. Check your browser permissions.");
    if (value === "connection-failed") toast.error("Voice transcription is unavailable right now. Please try again.");
  });
</script>

<template>
  <button
    type="button"
    data-cy="voice-input"
    :disabled="disabled || isStopping || isStarting"
    :title="title"
    class="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors"
    :class="[
      isRecording
        ? // Episto teal instead of a generic red, with a breathing ring and an
          // equaliser standing in for the mic. Literal hex rather than the
          // --ai-* tokens, which only exist where the assistant stylesheet is
          // loaded.
          [
            'relative bg-gradient-to-br from-[#019379] to-[#03d5af] text-white',
            'before:absolute before:-inset-[3px] before:rounded-full before:border-2',
            'before:border-[rgb(3_213_175_/_55%)] before:content-[\'\']',
            'before:animate-voice-ping motion-reduce:before:animate-none',
          ]
        : 'text-muted-foreground hover:bg-muted-foreground/10',
      disabled && 'cursor-not-allowed opacity-50',
    ]"
    @click="toggleRecording">
    <Loader2
      v-if="isStopping || isStarting"
      class="h-4 w-4 animate-spin" />
    <!-- Dictating: the mic gives way to an equalizer, so the button itself
         shows that something is being heard. -->
    <span
      v-else-if="isRecording"
      class="inline-flex h-3.5 items-center gap-0.5"
      aria-label="Listening">
      <i
        v-for="delay in ['0s', '0.12s', '0.24s', '0.36s']"
        :key="delay"
        class="block h-1 w-[2.5px] rounded-full bg-white animate-voice-wave motion-reduce:h-2.5 motion-reduce:animate-none"
        :style="{ animationDelay: delay }" />
    </span>
    <Mic
      v-else
      class="h-4 w-4" />
  </button>
</template>
